// Server-paginated prompt-library query plumbing, shared between PromptFlatTab
// (the only list consumer) and the route prefetcher so both build the exact
// same query key + request for the first page.

export const PROMPT_LIST_PAGE_SIZE = 50;

export interface PromptListParams {
  subject_id: string;
  competitor_offering_id: string;
  search: string;
  // Multi-select dimensions — sent as repeated query params (OR within a
  // dimension, AND across dimensions).
  category: string[];
  competitor_id: string[];
  feature_id: string[];
  persona: string[];
  /** Topic uuid(s); the literal 'none' selects the no-topic bucket. */
  topic_id: string[];
  /** The keyword a prompt was generated from — the Search Demand → keyword
   *  drill's scope. Matched exactly against `workspace_prompts.grounded_query`
   *  server-side, the same column the keyword header counts, so the table's
   *  row count and that header's `grounded_prompt_count` reconcile. */
  grounded_query: string;
  status: string[];
  tier: string[];
  starred: string;
  sort: string;
  direction: string;
}

/** The params PromptFlatTab requests on a clean first mount (no URL filters).
 *  `status: ['active']` mirrors parseFiltersFromUrl's DEFAULT_FILTER. */
export function defaultPromptListParams(subjectId?: string): PromptListParams {
  return {
    subject_id: subjectId ?? '',
    competitor_offering_id: '',
    search: '',
    category: [],
    competitor_id: [],
    feature_id: [],
    persona: [],
    topic_id: [],
    grounded_query: '',
    status: ['active'],
    tier: [],
    starred: '',
    sort: '',
    direction: '',
  };
}

export function buildPromptListPath(
  workspaceId: string,
  params: PromptListParams,
  offset: number,
): string {
  const qs = new URLSearchParams();
  qs.set('limit', String(PROMPT_LIST_PAGE_SIZE));
  qs.set('offset', String(offset));
  if (params.subject_id) qs.set('subject_id', params.subject_id);
  if (params.competitor_offering_id) qs.set('competitor_offering_id', params.competitor_offering_id);
  if (params.search) qs.set('search', params.search);
  params.category.forEach((v) => qs.append('category', v));
  params.competitor_id.forEach((v) => qs.append('competitor_id', v));
  params.feature_id.forEach((v) => qs.append('feature_id', v));
  params.persona.forEach((v) => qs.append('persona', v));
  params.topic_id.forEach((v) => qs.append('topic_id', v));
  if (params.grounded_query) qs.set('grounded_query', params.grounded_query);
  params.status.forEach((v) => qs.append('status', v));
  params.tier.forEach((v) => qs.append('tier', v));
  if (params.starred) qs.set('starred', params.starred);
  if (params.sort) {
    qs.set('sort', params.sort);
    qs.set('direction', params.direction || 'desc');
  }
  return `/workspaces/${workspaceId}/prompts?${qs.toString()}`;
}
